import { assertValue } from '@shared/helpers'
import { MINIMUM_MOVE_IN_YEAR_IN_PAST, SecLoansHousingLoanTargetType } from '@shared/sec-loans'
import { Bank } from '@shared/types'
import { MAX_REASONABLE_NUMBER } from '@shared/validation'
import { YesNo } from '@ui-common/types'
import { formatNumber } from '@ui-common/utils/helpers'
import {
  testIsValidMonetaryValue,
  testIsValidPercentageValue,
  validatePastMonthAndYear,
} from '@ui-common/utils/validation'
import { FieldName } from 'react-hook-form'
import { IntlShape } from 'react-intl'
import { create, enforce, omitWhen, only, Suite, test } from 'vest'

import { LoanTransferTargetInfoFormData as FormData } from './types'

export const createFormValidator = ({
  formatMessage,
  isFirstTimeHomeBuyerQuestionRequired,
  minimumHousingLoanAmount,
}: {
  formatMessage: IntlShape['formatMessage']
  isFirstTimeHomeBuyerQuestionRequired: boolean
  minimumHousingLoanAmount: number
}): Suite<string, string, (data: FormData, changedFields?: FieldName<FormData>[]) => void> => {
  return create((data, changedFields) => {
    only(changedFields)

    // ENUMS
    test('loanTargetType', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.loanTargetType).inside(Object.values(SecLoansHousingLoanTargetType))
    })

    omitWhen(!isFirstTimeHomeBuyerQuestionRequired, () => {
      test('isLoanForFirstTimePurchase', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.isLoanForFirstTimePurchase).inside(Object.values(YesNo))
      })

      omitWhen(data.isLoanForFirstTimePurchase !== YesNo.Yes, () => {
        test('isAspLoan', formatMessage({ id: 'validate-required-field-missing' }), () => {
          enforce(data.isAspLoan).inside(Object.values(YesNo))
        })
      })
    })

    test('loanIssuer', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.loanIssuer).inside(Object.values(Bank))
    })
    // END ENUMS

    // MONETARY
    testIsValidMonetaryValue('loanAmount', data.loanAmount, formatMessage, undefined, true, minimumHousingLoanAmount)
    testIsValidMonetaryValue('resaleEstimate', data.resaleEstimate, formatMessage)
    testIsValidMonetaryValue('purchasePrice', data.purchasePrice, formatMessage)
    testIsValidMonetaryValue('loanMonthlyPayment', data.loanMonthlyPayment, formatMessage, MAX_REASONABLE_NUMBER)

    test(
      'loanMonthlyPayment',
      formatMessage(
        { id: 'sec-loans-validate-value-is-not-more-than-other-value' },
        {
          firstValue: formatMessage({
            id: 'sec-loans-loan-transfer-target-loan-montly-installment-form-label',
          }),
          secondValue: formatMessage({ id: 'sec-loans-loan-transfer-target-loan-amount-form-label' }),
        },
      ),
      () => {
        const loanMonthlyPayment = assertValue(data.loanMonthlyPayment, 'data.loanMonthlyPayment')
        const loanAmount = assertValue(data.loanAmount, 'data.loanAmount')
        enforce(formatNumber(loanMonthlyPayment) <= formatNumber(loanAmount)).isTruthy()
      },
    )
    // END MONETARY

    // PERCENTAGE
    testIsValidPercentageValue('loanMarginPercentage', data.loanMarginPercentage, formatMessage, false)
    // END PERCENTAGE

    // TEXT MONTH YEAR
    validatePastMonthAndYear({
      fieldName: 'moveInMonth',
      formatMessage,
      dateToValidate: data.moveInMonth,
      minimumYear: MINIMUM_MOVE_IN_YEAR_IN_PAST,
      isRequired: true,
    })
    // END TEXT MONTH YEAR
  })
}
